import PropTypes from 'prop-types';
import React, { useState } from 'react';

import Button from '$partials/ui/button';
import { Footer, Header, Meta } from '$partials/global';
import { getAllModules } from '$utilities/data';
import Image from '$partials/media/image';
import ModuleDispatcher from '$partials/modules/dispatcher';
import IconArrowRight from '$partials/icons/arrow-right';
import { HeaderCardItem } from '$partials/modules/individual/index.js';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const HomePage = ({ data, domain }) => {
    const page = data?.page;

    const [isOpen, setIsOpen] = useState(false);

    if (!page) {return null;}

    const modules = getAllModules(page);


    return (
        <>
            <Meta page={page} />

            <main id='main-content'>
                <div className='min-h-screen overflow-hidden'>
                    <Header
                        isOpen={isOpen}
                        menu={data?.navigation}
                        setIsOpen={setIsOpen}/>

                    {/* Header background image */}
                    {page.headerBackgroundImage && (
                        <figure className='max-w-full w-screen absolute left-1/2 -translate-x-1/2 -z-10 mt-[272px] md:mt-100'>
                            <div className='h-full '>
                                <Image
                                    {...page.headerBackgroundImage}
                                    className='w-full h-full object-cover'
                                    containerClassName='w-full h-full relative'
                                    height='100%'
                                    loadWidth={2560}
                                    width='100%' />
                            </div>
                        </figure>
                    )}
                    <div className='layout-container z-10 relative mb-40 md:mb-50'>
                        <div className='w-full flex flex-col mt-60 md:mt-80'>
                            {/* Header text space */}
                            <div className='flex w-full z-10 px-10 sm:px-5 md:px-10'>
                                {/* Spacer */}
                                <div className='hidden md:flex w-1/12 px-10'/>
                                <div className='w-full md:w-10/12 flex flex-col justify-center items-center'>
                                    <h1 className='type-style-1 text-center'>
                                        {page.headerTitle}
                                    </h1>
                                    {page.headerText && (
                                        <p className='type-style-5 px-10 sm:px-0 sm:w-8/12 md:w-1/2 mt-15 text-center'>
                                            {page.headerText}
                                        </p>
                                    )}
                                    {page.ctaLabel && page.ctaLink && (
                                        <Button
                                            as='a'
                                            className='mt-25 md:mt-20 flex items-center bg-orange text-white rounded-button type-style-7 px-20 py-15 w-auto'
                                            href={page.ctaLink}
                                            renderIcon={<IconArrowRight className='h-13'/>}>
                                            {page.ctaLabel}
                                        </Button>
                                    )}
                                </div>
                            </div>
                            {/* Header image cards */}
                            {page.headerCards?.items?.length > 0 && (
                                <div className='flex flex-wrap sm:w-full mt-50 sm:mt-80 md:mt-100 -mx-10 sm:mx-0 sm:gap-y-10 md:gap-y-20'>
                                    {page.headerCards.items.map((card, id) => {
                                        return (
                                            <HeaderCardItem
                                                index={id}
                                                key={id}
                                                {...card}/>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Content */}
                    {(modules?.length > 0) && (
                        <section>
                            {modules.map((module, i) => {
                                if (!module) {return null;}
                                return (
                                    <ModuleDispatcher
                                        key={i}
                                        module={module}
                                        nextTypename={modules[i + 1]?.__typename}
                                        parent={'Home'}
                                        previousTypename={modules[i - 1]?.__typename} />
                                );
                            })}
                        </section>
                    )}
                    <div className='layout-container pt-30 sm:pt-0 md:pt-20'>
                        <Footer
                            domain={domain}
                            menu={data?.footer} />
                    </div>
                </div>
            </main>
        </>
    );
};

/* =========================================================================  */
/* Typing and exports */
/* =========================================================================  */

const propTypes = {
    data: PropTypes.shape({
        page: PropTypes.shape({
            modules: PropTypes.shape({
                items: PropTypes.array
            })
        }),
        footer: PropTypes.shape({
            items: PropTypes.array
        }),
        navigation: PropTypes.object,
    }),
    domain: PropTypes.string,
};

HomePage.propTypes = propTypes;

export default HomePage;
